import { createSelector } from '@reduxjs/toolkit'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Box3, Group, Vector3, Vector3Like } from 'three'
import { useProject } from '~/components/DrawingCanvas/hooks'
import { ToolManagerRef } from '~/components/DrawingCanvas/ToolManager'
import { scaleModelVectorToUI } from '~/components/DrawingCanvas/util/units'
import ObjectClassName from '~/config/objectClassNames'
import theme from '~/config/theme'
import { RootState } from '~/store'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { selectObjects } from '~/store/selectedObjects/actions'

const SELECTION_COLOR = theme.colors.three.selectionBox
const SELECTION_HEIGHT = 250
const SELECTION_THRESHOLD = 3
const tempObjectPosition = new Vector3()
const min = new Vector3()
const max = new Vector3()

type SelectableObject = {
  id: string
  className: Extract<ObjectClassName, 'Product' | 'Obstruction'>
  position: Vector3Like
}
const selectSelectableObjects = createSelector(
  (state: RootState) => state.objects.present.products,
  (state: RootState) => state.objects.present.obstructions,
  (state: RootState) => state.layers.layers,
  (products, obstructions, layers) => {
    const { PRODUCT, OBSTRUCTION } = ObjectClassName
    const selectableProducts = Object.values(products)
      .filter(({ layerKey }) => layers[layerKey].visible)
      .map<SelectableObject>(({ id, position }) => ({ id, className: PRODUCT, position }))
    const selectableObstructions = layers.OBSTRUCTIONS.visible
      ? Object.values(obstructions).map<SelectableObject>(({ id, position }) => ({
          id,
          className: OBSTRUCTION,
          position,
        }))
      : []
    return selectableProducts.concat(selectableObstructions)
  }
)

export const SelectTool = forwardRef<ToolManagerRef, { activeTool: any; activeToolProps: unknown }>((_, ref) => {
  const dispatch = useAppDispatch()
  const project = useProject()

  const selectableObjects = useAppSelector(selectSelectableObjects)
  const isActiveSelection = useAppSelector(state => state.selectedObjects.length > 0)
  const isMoveTool = useAppSelector(state => state.tools.activeTool === 'MOVE_TOOL')

  const start = useRef(new Vector3())
  const end = useRef(new Vector3())
  const box = useRef<Box3>(new Box3())
  const groupRef = useRef<Group>(null!)

  const isTap = (boxSize: Vector3) => boxSize.x ** 2 + boxSize.y ** 2 < SELECTION_THRESHOLD

  useImperativeHandle(
    ref,
    () => ({
      onMouseDown(event) {
        if (isActiveSelection || isMoveTool) return false
        const point = project(event)
        if (point) start.current.copy({ ...point, z: 0 })
        return false
      },
      onMouseMove(event) {
        const point = project(event)
        if (point && start.current.length()) {
          end.current.copy({ ...point, z: 0 })
          min.set(Math.min(start.current.x, end.current.x), Math.min(start.current.y, end.current.y), 0)
          max.set(Math.max(start.current.x, end.current.x), Math.max(start.current.y, end.current.y), SELECTION_HEIGHT)
          box.current.set(min, max)
          const boxMesh = groupRef.current.children[0]
          box.current.getSize(boxMesh.scale)
          box.current.getCenter(boxMesh.position)
          groupRef.current.visible = !isTap(boxMesh.scale)
        }
        return false
      },
      onMouseUp() {
        const boxMesh = groupRef.current.children[0]
        if (!isTap(boxMesh.scale) && !isActiveSelection) {
          dispatch(
            selectObjects({
              objects: selectableObjects.filter(({ position }) => {
                scaleModelVectorToUI(tempObjectPosition.copy(position))
                return box.current.containsPoint(tempObjectPosition)
              }),
            })
          )
        }
        start.current.set(0, 0, 0)
        end.current.set(0, 0, 0)
        groupRef.current.visible = false
        return false
      },
    }),
    [project, selectableObjects, isActiveSelection, isMoveTool]
  )

  return (
    <group visible={false} ref={groupRef}>
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial transparent depthWrite={false} opacity={0.1} color={SELECTION_COLOR} />
      </mesh>
      <box3Helper args={[box.current, SELECTION_COLOR]} />
    </group>
  )
})
